import useStudioStore from './useStudioStore';
import { resetSupportContext } from '../support/context';
import { create } from 'zustand';

import {
  loadSpaces,
  onPersistenceNamespaceChange,
} from './helpers/persistenceHelpers';
import { createNavigationSlice } from './slices/navigationSlice';
import { createDefinitionsSlice } from './slices/definitionsSlice';
import { createLandingSlice } from './slices/landingSlice';
import { createThreadsSlice } from './slices/threadsSlice';
import { createPinsSlice } from './slices/pinsSlice';
import { createReviewsSlice } from './slices/reviewsSlice';
import { createWorkspaceSlice } from './slices/workspaceSlice';
import { createReportsSlice } from './slices/reportsSlice';
import { createSettingsSlice } from './slices/settingsSlice';
import { createLandingAgentSlice } from './slices/landingAgentSlice';
import { createWorkspaceAgentSlice } from './slices/workspaceAgentSlice';
import { createWorkflowsSlice } from './slices/workflowsSlice';
import { createConnectionsSlice } from './slices/connectionsSlice';
import { createWorkflowComposerSlice } from './slices/workflowComposerSlice';
import { createDashboardsSlice } from './slices/dashboardsSlice';
import { createAgentsSlice } from './slices/agentsSlice';
import { createAgentStudioSlice } from './slices/agentStudioSlice';
import { createFeedSlice } from './slices/feedSlice';
import { createFeaturesSlice } from './slices/featuresSlice';

import type { NavigationSlice } from './slices/navigationSlice';
import type { DefinitionsSlice } from './slices/definitionsSlice';
import type { LandingSlice } from './slices/landingSlice';
import type { ThreadsSlice } from './slices/threadsSlice';
import type { PinsSlice } from './slices/pinsSlice';
import type { ReviewsSlice } from './slices/reviewsSlice';
import type { WorkspaceSlice } from './slices/workspaceSlice';
import type { ReportsSlice } from './slices/reportsSlice';
import type { SettingsSlice } from './slices/settingsSlice';
import type { LandingAgentSlice } from './slices/landingAgentSlice';
import type { WorkspaceAgentSlice } from './slices/workspaceAgentSlice';
import type { WorkflowsSlice } from './slices/workflowsSlice';
import type { ConnectionsSlice } from './slices/connectionsSlice';
import type { WorkflowComposerSlice } from './slices/workflowComposerSlice';
import type { DashboardsSlice } from './slices/dashboardsSlice';
import type { AgentsSlice } from './slices/agentsSlice';
import type { AgentStudioSlice } from './slices/agentStudioSlice';
import type { FeedSlice } from './slices/feedSlice';
import type { FeaturesSlice } from './slices/featuresSlice';

export type VoyagerState =
  NavigationSlice &
  DefinitionsSlice &
  LandingSlice &
  ThreadsSlice &
  PinsSlice &
  ReviewsSlice &
  WorkspaceSlice &
  ReportsSlice &
  SettingsSlice &
  LandingAgentSlice &
  WorkspaceAgentSlice &
  WorkflowsSlice &
  ConnectionsSlice &
  WorkflowComposerSlice &
  DashboardsSlice &
  AgentsSlice &
  AgentStudioSlice &
  FeedSlice &
  FeaturesSlice;

const useVoyagerStore = create<VoyagerState>()((...args) => ({
  ...createNavigationSlice(...args),
  ...createDefinitionsSlice(...args),
  ...createLandingSlice(...args),
  ...createThreadsSlice(...args),
  ...createPinsSlice(...args),
  ...createReviewsSlice(...args),
  ...createWorkspaceSlice(...args),
  ...createReportsSlice(...args),
  ...createSettingsSlice(...args),
  ...createLandingAgentSlice(...args),
  ...createWorkspaceAgentSlice(...args),
  ...createWorkflowsSlice(...args),
  ...createConnectionsSlice(...args),
  ...createWorkflowComposerSlice(...args),
  ...createDashboardsSlice(...args),
  ...createAgentsSlice(...args),
  ...createAgentStudioSlice(...args),
  ...createFeedSlice(...args),
  ...createFeaturesSlice(...args),
}));

// Spaces are namespaced per signed-in account (see persistenceHelpers) and
// threads are fetched per account + workspace from the server (#181). The
// store initializes before Clerk resolves the user, so reload both whenever
// the identity namespace lands/changes.
//
// The reset also clears the conversation on screen and bumps the threads
// generation: a transcript from the previous account or org must neither be
// persisted into this workspace nor arrive late from an in-flight request.
onPersistenceNamespaceChange(() => {
  resetSupportContext();
  useStudioStore.getState().cancelStudioChatLocal();
  useStudioStore.setState({ draftModel: null, baselineModel: null, isLoading: false, error: null, isDirty: false, studioChatMessages: [], sourceContext: null, draftsList: [] });
  useVoyagerStore.setState({ currentDashboard: null, dashboardsLoading: false, currentWorkflow: null, semanticModel: null, isLoading: false, error: null, loadedReportId: null, loadedReportName: null, isDirty: false });
  useVoyagerStore.getState().resetThreadsWorkspace();
  // Agents, their walls and details (#200/#202) belong to the workspace too.
  useVoyagerStore.getState().resetAgentsWorkspace();
  // So do the Studio's connectors, grants and audit (#198) — and the
  // connections, ledgers and open drawer they are read through.
  useVoyagerStore.getState().resetStudioWorkspace();
  useVoyagerStore.getState().resetConnectionsWorkspace();
  // The feed (#201) is disclosed under the workspace's evidence policy — drop it too.
  useVoyagerStore.getState().resetFeedWorkspace();
  useVoyagerStore.getState().resetFeatures();
  useVoyagerStore.setState({ spaces: loadSpaces(), brainMetric: null, brainLens: null, newAgentObjective: null, activityAgentId: null, connectionWizardProvider: null } as Partial<VoyagerState>);
  useVoyagerStore.getState().fetchThreads();
});

export { useVoyagerStore };
export default useVoyagerStore;
